import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from "context/AuthContext";
import {
  Box,
  Typography,
  Card,
  Avatar,
  IconButton,
  Popover,
  Dialog,
  DialogTitle,
  DialogContent
} from '@mui/material';
import { NotificationsOutlined, Close } from '@mui/icons-material';
import ArgonButton from "components/ArgonButton";
import { ConsultasSkeleton } from "components/SkeletonLoader";
import consultasIllustration from "assets/illustrations/consultas.svg";

const Consultas = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading, getToken } = useAuth();
  const [consultas, setConsultas] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1100);
  const [notificationsAnchorEl, setNotificationsAnchorEl] = useState(null);
  const [selectedConsulta, setSelectedConsulta] = useState(null);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [usersInfo, setUsersInfo] = useState({}); // Almacenar info de usuarios por userId
  const profesionalId = localStorage.getItem('id');
  const baseURL = process.env.REACT_APP_BASE_URL || "https://sandbox.payflexapp.com";

  useEffect(() => {
    // Kickout solo si Firebase confirma que no hay usuario autenticado
    if (!authLoading && !user) {
      navigate("/authentication/sign-in");
    }
  }, [navigate, user, authLoading]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 1100);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!user || authLoading || !profesionalId) {
      return;
    }

    const fetchConsultas = async () => {
      setIsLoading(true);
      try {
        const token = await getToken();
        if (!token) {
          navigate("/authentication/sign-in");
          return;
        }

        // Función para obtener información de un usuario por su ID
        const fetchUserInfo = async (userId) => {
          try {
            // Intentar diferentes endpoints posibles
            const endpoints = [
              `${baseURL}/usuarios/${userId}`,
              `${baseURL}/usuario/${userId}`,
              `${baseURL}/users/${userId}`,
              `${baseURL}/user/${userId}`
            ];

            for (const endpoint of endpoints) {
              try {
                const response = await fetch(endpoint, {
                  method: 'GET',
                  headers: { 
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                  },
                });
                
                if (response.ok) {
                  const userData = await response.json();
                  return userData;
                }
              } catch (err) {
                // Continuar con el siguiente endpoint si este falla
                continue;
              }
            }
            
            return null;
          } catch (error) {
            console.error(`Error al obtener información del usuario ${userId}:`, error);
            return null;
          }
        };

        const response = await fetch(`${baseURL}/agendas-profesional/${profesionalId}`, {
          method: 'GET',
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
        
        if (response.ok) {
          const data = await response.json();
          setConsultas(data);

          // Obtener userIds únicos de las consultas
          const userIds = [...new Set(data
            .map(consulta => consulta.userId || consulta.user_id || consulta.User?.id)
            .filter(id => id !== null && id !== undefined)
          )];

          // Obtener información de cada usuario
          const usersData = {};
          await Promise.all(
            userIds.map(async (userId) => {
              const userInfo = await fetchUserInfo(userId);
              if (userInfo) {
                usersData[userId] = userInfo;
              }
            })
          );

          setUsersInfo(usersData);
        } else {
          console.error('Error al obtener consultas');
        }
      } catch (error) {
        console.error('Error al obtener consultas', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchConsultas();
  }, [profesionalId, user, authLoading, getToken, navigate, baseURL]);

  const formatearFecha = (fecha) => {
    if (!fecha) return '';
    const [year, month, day] = fecha.split('-');
    return `${day}/${month}/${year.slice(-2)}`;
  };

  const formatearHora = (hora) => {
    if (!hora) return '';
    return hora.slice(0, 5); // Mantiene solo HH:mm
  };

  const calcularHoraFin = (horaInicio, duracion) => {
    if (!horaInicio || !duracion) return '';
    
    const [horas, minutos] = horaInicio.split(':').map(Number);
    const totalMinutos = horas * 60 + minutos + duracion;
    const nuevasHoras = Math.floor(totalMinutos / 60);
    const nuevosMinutos = totalMinutos % 60;
    
    return `${nuevasHoras.toString().padStart(2, '0')}:${nuevosMinutos.toString().padStart(2, '0')}`;
  };

  const obtenerIniciales = (nombre, apellido) => {
    if (!nombre || !apellido) return 'U';
    return `${nombre.charAt(0)}${apellido.charAt(0)}`.toUpperCase();
  };

  const filtrarConsultasProximas = (consultas) => {
    const ahora = new Date();
    const hoy = ahora.toISOString().split('T')[0]; // Formato YYYY-MM-DD
    
    return consultas.filter(consulta => {
      const fechaConsulta = new Date(consulta.fecha + 'T' + consulta.hora);
      const diferenciaMinutos = (fechaConsulta - ahora) / (1000 * 60);
      
      // Si es del día de hoy: mostrar todas las futuras + las pasadas hace menos de 1 hora
      if (consulta.fecha === hoy) {
        return diferenciaMinutos >= -60;
      }
      
      // Si es de días posteriores: mostrar todas las consultas futuras
      return diferenciaMinutos > 0;
    });
  };

  const handleNotificationsClick = (event) => {
    setNotificationsAnchorEl(event.currentTarget);
  };

  const handleNotificationsClose = () => {
    setNotificationsAnchorEl(null);
  };

  const handleGoogleMeetClick = (meetLink) => {
    if (meetLink) {
      window.open(meetLink, '_blank', 'noopener,noreferrer');
    }
  };

  const handleDetailsClick = (consulta) => {
    setSelectedConsulta(consulta);
    setDetailsModalOpen(true);
  };

  const handleCloseDetailsModal = () => {
    setDetailsModalOpen(false);
    // Limpiar el estado después de que el modal se haya cerrado
    setTimeout(() => {
      setSelectedConsulta(null);
    }, 300);
  };

  if (authLoading || isLoading) {
    return <ConsultasSkeleton />;
  }

  // Si no hay usuario después de cargar, no renderizar nada (el useEffect redirigirá)
  if (!user) {
    return null;
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: "#FFFFFF",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: "20px",
        paddingBottom: "100px", // Espacio para el tabbar
        "&::-webkit-scrollbar": {
          display: "none",
        },
        scrollbarWidth: "none",
        msOverflowStyle: "none",
      }}
    >
      {/* Header con título centrado y notificación */}
      <Box sx={{ 
        width: "100%",
        maxWidth: "800px",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        marginBottom: "40px",
        marginTop: "20px",
        position: "relative",
        px: 2
      }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: "bold",
            color: "#000",
            fontSize: { xs: "20px", md: "24px" },
            textAlign: "center",
          }}
        >
          Consultas
        </Typography>
        <IconButton 
          onClick={handleNotificationsClick}
          sx={{ 
            color: "#666",
            position: "absolute",
            right: 0,
          }}
        >
          <NotificationsOutlined />
        </IconButton>
      </Box>

      {/* Sección de Próximas Consultas */}
      {(() => {
        const consultasProximas = filtrarConsultasProximas(consultas);
        return consultasProximas.length === 0 ? (
        <Box sx={{ 
          width: "100%",
          maxWidth: "800px",
          px: 2,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "flex-start",
          flex: 1,
          minHeight: "calc(100vh - 200px)",
          paddingTop: "10vh"
        }}>
          <Card sx={{
            borderRadius: '16px',
            boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
            backgroundColor: '#FFFFFF',
            p: 4,
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '400px'
          }}>
            {/* Ilustración */}
            <Box sx={{ mb: 3 }}>
              <img 
                src={consultasIllustration} 
                alt="Sin consultas agendadas"
                style={{
                  width: '100%',
                  maxWidth: '300px',
                  height: 'auto'
                }}
              />
            </Box>
            
            {/* Texto descriptivo */}
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 'bold',
                color: '#000',
                fontSize: isMobile ? '18px' : '20px',
                mb: 1
              }}
            >
              ¡Todo listo para empezar!
            </Typography>
            
            <Typography 
              variant="body1" 
              sx={{ 
                color: '#666',
                fontSize: isMobile ? '14px' : '16px',
                lineHeight: 1.5,
                maxWidth: '280px'
              }}
            >
              Aquí se desplegará la información de cada consulta confirmada.
            </Typography>
          </Card>
        </Box>
      ) : (
        <Box sx={{ 
          width: "100%",
          maxWidth: "800px",
          px: 2
        }}>
          <Typography 
            variant="h6" 
            sx={{ 
              fontWeight: '600',
              mb: 2,
              color: '#000',
              fontSize: isMobile ? '16px' : '18px'
            }}
          >
            Próximas consultas
          </Typography>
          {consultasProximas.map((consulta, index) => {
            // Obtener userId de la consulta
            const userId = consulta.userId || consulta.user_id || consulta.User?.id;
            // Obtener información del usuario desde usersInfo o desde consulta.User
            const userInfo = userId && usersInfo[userId] 
              ? usersInfo[userId] 
              : consulta.User || {};
            
            return (
            <Card
              key={consulta.id}
              sx={{
                mb: 2,
                p: 2,
                borderRadius: '12px',
                boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.1)',
                border: 'none',
                backgroundColor: '#fff'
              }}
            >
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center',
                justifyContent: 'space-between'
              }}>
                {/* Avatar y Nombre */}
                <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                  <Avatar
                    src={userInfo.imagen || userInfo.foto || userInfo.profilePicture}
                    sx={{ 
                      width: 65, 
                      height: 65, 
                      mr: 2,
                      backgroundColor: '#1F85F6',
                      fontSize: '18px',
                      fontWeight: 'bold'
                    }}
                  >
                    {userInfo.imagen || userInfo.foto || userInfo.profilePicture ? null : obtenerIniciales(userInfo.nombre, userInfo.apellido)}
                  </Avatar>
                  
                  <Box sx={{ flex: 1 }}>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        fontWeight: 'bold',
                        fontSize: isMobile ? '16px' : '18px',
                        color: '#000',
                        mb: 0.5
                      }}
                    >
                      {userInfo.nombre || ''} {userInfo.apellido || ''}
                    </Typography>
                  </Box>
                </Box>

                {/* Iconos de Acción */}
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  gap: 1
                }}>
                  
                 {/*
                    <IconButton sx={{ color: '#666', p: 0.5 }}>
                      <FontAwesomeIcon icon={faComment} size="xs" />
                    </IconButton>
                
                  <IconButton sx={{ color: '#666', p: 0.5 }}>
                    <FontAwesomeIcon icon={faEdit} size="xs" />
                  </IconButton>
                    */}
                  <button
                    onClick={() => handleDetailsClick(consulta)}
                    style={{
                      backgroundColor: '#EBF4FF',
                      color: '#1F85F6',
                      borderRadius: '20px',
                      padding: '4px 12px',
                      fontSize: '11px',
                      fontWeight: '600',
                      textTransform: 'none',
                      border: 'none',
                      whiteSpace: 'nowrap',
                      height: '28px',
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                      outline: 'none',
                      transition: 'all 0.2s ease',
                    }}
                    onMouseDown={(e) => {
                      e.target.style.backgroundColor = '#1A6BC7';
                      e.target.style.color = '#FFFFFF';
                    }}
                    onMouseUp={(e) => {
                      e.target.style.backgroundColor = '#EBF4FF';
                      e.target.style.color = '#1F85F6';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = '#EBF4FF';
                      e.target.style.color = '#1F85F6';
                    }}
                  >
                    + Detalles
                  </button>
                </Box>
              </Box>

              {/* Línea divisora */}
              <Box sx={{ 
                height: '1px',
                backgroundColor: '#F0F0F0',
                mx: 0.5,
                mt: 1.5
              }} />

              {/* Fecha y Hora */}
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between',
                mt: 1.5,
                px: 0.5
              }}>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: '#666',
                    fontSize: isMobile ? '12px' : '14px'
                  }}
                >
                  Fecha: {formatearFecha(consulta.fecha)}
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: '#666',
                    fontSize: isMobile ? '12px' : '14px'
                  }}
                >
                  Hora: {formatearHora(consulta.hora)} hrs - {calcularHoraFin(consulta.hora, consulta.duracion)} hrs
                </Typography>
              </Box>
            </Card>
            );
          })}
        </Box>
        );
      })()}

      {/* Popover de notificaciones */}
      <Popover
        open={Boolean(notificationsAnchorEl)}
        anchorEl={notificationsAnchorEl}
        onClose={handleNotificationsClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        sx={{
          zIndex: 9999,
          '& .MuiPopover-paper': {
            borderRadius: '8px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
            border: '1px solid #e0e0e0',
            backgroundColor: '#ffffff',
            minWidth: '200px',
            zIndex: 9999,
          }
        }}
      >
        <Box sx={{ p: 2, textAlign: 'center' }}>
          <Typography variant="body2" sx={{ color: '#666', fontSize: '14px' }}>
            No hay notificaciones pendientes
          </Typography>
        </Box>
      </Popover>

      {/* Modal de detalles de consulta */}
      <Dialog
        open={detailsModalOpen}
        onClose={handleCloseDetailsModal}
        maxWidth="sm"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: '12px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
            margin: '16px'
          }
        }}
      >
        <DialogTitle sx={{ 
          pb: 1.5,
          px: 3,
          pt: 3,
          fontSize: '16px',
          fontWeight: '600',
          color: '#000',
          borderBottom: '1px solid #E0E0E0',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: '#FAFBFC',
          position: 'relative'
        }}>
          Detalles de la consulta
          <IconButton
            onClick={handleCloseDetailsModal}
            sx={{
              color: '#666',
              padding: '6px',
              borderRadius: '8px',
              position: 'absolute',
              right: '12px',
              '&:hover': {
                backgroundColor: '#E0E0E0',
                color: '#000'
              }
            }}
          >
            <Close fontSize="small" />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ p: 2 }}>
          {selectedConsulta && (() => {
            // Obtener userId de la consulta seleccionada
            const userId = selectedConsulta.userId || selectedConsulta.user_id || selectedConsulta.User?.id;
            // Obtener información del usuario desde usersInfo o desde selectedConsulta.User
            const userInfo = userId && usersInfo[userId] 
              ? usersInfo[userId] 
              : selectedConsulta.User || {};
            
            return (
            <Box>
              {/* Información del paciente */}
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column',
                alignItems: 'center', 
                p: 1.5,
                backgroundColor: '#FFFFFF',
                borderRadius: '8px',
                mb: 0.5
              }}>
                <Avatar
                  src={userInfo.imagen || userInfo.foto || userInfo.profilePicture}
                  sx={{ 
                    width: 110, 
                    height: 110, 
                    mb: 1.5,
                    backgroundColor: '#1F85F6',
                    fontSize: '28px',
                    fontWeight: 'bold'
                  }}
                >
                  {userInfo.imagen || userInfo.foto || userInfo.profilePicture ? null : obtenerIniciales(userInfo.nombre, userInfo.apellido)}
                </Avatar>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="body1" sx={{ 
                    fontWeight: '700', 
                    color: '#000', 
                    fontSize: '18px',
                    lineHeight: 1.2,
                    mb: 0.5
                  }}>
                    {userInfo.nombre || ''} {userInfo.apellido || ''}
                  </Typography>
                  <Typography variant="body2" sx={{ 
                    color: '#666',
                    fontSize: '16px',
                    mb: 0.25
                  }}>
                    {formatearFecha(selectedConsulta.fecha)}
                  </Typography>
                  <Typography variant="body2" sx={{ 
                    color: '#666',
                    fontSize: '16px'
                  }}>
                    {formatearHora(selectedConsulta.hora)} - {calcularHoraFin(selectedConsulta.hora, selectedConsulta.duracion)}
                  </Typography>
                </Box>
              </Box>

    

              {/* Botón de Google Meet si hay meetLink */}
              {selectedConsulta.meetLink && (
                <ArgonButton
                  variant="contained"
                  color="primary"
                  size="large"
                  onClick={() => handleGoogleMeetClick(selectedConsulta.meetLink)}
                  sx={{
                    backgroundColor: '#F8F9FA',
                    color: '#000000',
                    borderRadius: '12px',
                    px: 3,
                    py: 2,
                    fontSize: '16px',
                    fontWeight: '600',
                    textTransform: 'none',
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1.5,
                    mt: 1,
                    minHeight: '56px',
                    border: '1px solid #D0D0D0',
                    boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.25)',
                    '&:hover': {
                      backgroundColor: '#F0F0F0',
                      color: '#000000',
                      boxShadow: '0px 12px 32px rgba(0, 0, 0, 0.35)',
                    }
                  }}
                >
                  <Box
                    component="img"
                    src="https://iili.io/KcVr9zx.webp"
                    alt="Google Meet"
                    sx={{
                      width: "18px",
                      height: "18px",
                      borderRadius: "2px"
                    }}
                  />
                  Unirse a Google Meet
                </ArgonButton>
              )}
            </Box>
            );
          })()}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default Consultas;
